import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { ArrowLeft, ArrowRight, FloppyDisk, PaperPlaneTilt, Sparkle, PaperPlane, Paperclip, X, PencilSimple, UsersThree, RocketLaunch, CheckCircle, Circle, Clock, ArrowBendUpLeft, Copy, FilePdf, CaretRight, CaretDown, Fire } from "@phosphor-icons/react";
import { toast } from "sonner";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { PreflightModal } from "../components/PreflightModal";
import { VerifyRecipientsCard } from "../components/VerifyRecipientsCard";
import { EmailBuilder } from "../components/EmailBuilder";
import { FollowUpDialog } from "../components/FollowUpDialog";
import { ContactQuickView } from "../components/ContactQuickView";

export default function CampaignDetailPage() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [lists, setLists] = useState([]);
  const [segments, setSegments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [scanner, setScanner] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [folderSkipped, setFolderSkipped] = useState(0);
  const [aiBusy, setAiBusy] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [tab, setTab] = useState("compose");
  const [step, setStep] = useState(1);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [health, setHealth] = useState(null);
  const [preflightOpen, setPreflightOpen] = useState(false);
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [stopArmed, setStopArmed] = useState(false);
  const [preview, setPreview] = useState(null);
  const quillRef = useRef(null);
  const subjectRef = useRef(null);
  const [lastFocus, setLastFocus] = useState("body");
  const [appSettings, setAppSettings] = useState(null);
  const [abStatus, setAbStatus] = useState(null);
  const [btnDialogOpen, setBtnDialogOpen] = useState(false);
  const [btnLabel, setBtnLabel] = useState("Schedule a demo");
  const [btnUrl, setBtnUrl] = useState("https://");
  const [liveCount, setLiveCount] = useState(null);

  const load = () => api.getCampaign(id).then(setC);
  const loadRecipients = () => {
    api.campaignRecipients(id).then(d => setRecipients(d.items || []));
    api.scannerStats(id).then(setScanner).catch(() => {});
  };
  const loadAttachments = () => api.listAttachments(id).then(d => setAttachments(d.items || []));
  const loadHealth = () => api.campaignHealth(id).then(setHealth).catch(() => {});

  useEffect(() => {
    setC(null);
    setStep(1);
    setTab("compose");
    setLiveCount(null);
    setRecipients([]);
    setScanner(null);
    setScannerOpen(false);
    setPreview(null);
    setFollowUpOpen(false);
    load();
    api.listLists().then(setLists);
    api.listSegments().then(setSegments);
    api.listTemplates().then(setTemplates);
    api.settings().then(setAppSettings).catch(() => {});
    loadAttachments();
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (c?.status === "sent" || c?.status === "sending") {
      loadRecipients();
      loadHealth();
    }
    // eslint-disable-next-line
  }, [c?.status]);

  useEffect(() => {
    if (!c?.ab) { setAbStatus(null); return; }
    const fetchAb = () => api.abStatus(id).then(setAbStatus).catch(() => {});
    fetchAb();
    if (c.status === "sending") {
      const t = setInterval(() => { fetchAb(); load(); }, 30000);
      return () => clearInterval(t);
    }
    // eslint-disable-next-line
  }, [id, c?.status, !!c?.ab]);

  useEffect(() => {
    if (!c) return;
    const t = setTimeout(() => {
      api.renderPreview({ subject: c.subject || "", html: c.html || "", from_name: c.from_name, from_email: c.from_email, editor_mode: c.editor_mode || "richtext", blocks: c.blocks || [] })
        .then(setPreview)
        .catch(() => {});
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [c?.subject, c?.html, c?.from_name, c?.from_email, JSON.stringify(c?.blocks)]);

  useEffect(() => {
    if (!c) return;
    const t = setTimeout(() => {
      api.audienceCount({ audience_type: c.audience_type, audience_id: c.audience_id || null, audience_ids: c.audience_ids || [], audience_filter: c.audience_filter || null, followup_criteria: c.followup_criteria || null, group_id: c.group_id || null, campaign_id: c.id, exclude_folder_sent: c.exclude_folder_sent !== false })
        .then(d => { setLiveCount(d.count); setFolderSkipped(d.folder_skipped || 0); })
        .catch(() => setLiveCount(null));
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [c?.audience_type, c?.audience_id, c?.audience_filter, c?.followup_criteria, c?.group_id, c?.exclude_folder_sent]);

  const QUICK_FILTERS = { engaged_90d: "Engaged in the last 90 days", highly_engaged: "Highly engaged (score ≥ 10)", never_opened: "Never opened an email" };
  const filterLabel = (f) => !f ? null : (f.startsWith("segment:") ? `Segment: ${segments.find(s => s.id === f.slice(8))?.name || "unknown"}` : (QUICK_FILTERS[f] || f));
  const FOLLOWUP_LABELS = { not_opened: "didn't open", opened_no_click: "opened but didn't click", not_clicked: "didn't click" };
  const audienceLabel = () => {
    let base = "No audience yet";
    if (c?.audience_type === "all") base = "All active contacts";
    else if (c?.audience_type === "list") base = c.audience_id ? `List: ${lists.find(l => l.id === c.audience_id)?.name || "unknown"}` : "List not chosen";
    else if (c?.audience_type === "segment") base = c.audience_id ? `Segment: ${segments.find(s => s.id === c.audience_id)?.name || "unknown"}` : "Segment not chosen";
    else if (c?.audience_type === "followup") base = `Follow-up of "${c.followup_source_name || "original campaign"}" — recipients who ${FOLLOWUP_LABELS[c.followup_criteria] || "didn't open"}`;
    const fl = filterLabel(c?.audience_filter);
    return fl ? `${base} → narrowed to: ${fl}` : base;
  };

  const insertField = (field) => {
    const tag = `{{${field}}}`;
    if (c?.editor_mode === "blocks") {
      navigator.clipboard.writeText(tag);
      toast.success(`${tag} copied — paste it into any text field`);
      return;
    }
    if (lastFocus === "subject" && subjectRef.current) {
      const el = subjectRef.current;
      const start = el.selectionStart ?? (c.subject || "").length;
      const end = el.selectionEnd ?? start;
      update({ subject: (c.subject || "").slice(0, start) + tag + (c.subject || "").slice(end) });
      requestAnimationFrame(() => { el.focus(); el.setSelectionRange(start + tag.length, start + tag.length); });
    } else {
      const editor = quillRef.current?.getEditor();
      if (!editor) return;
      const range = editor.getSelection(true) || { index: editor.getLength() - 1 };
      editor.insertText(range.index, tag, "user");
      editor.setSelection(range.index + tag.length);
    }
  };

  const insertBlock = (html) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const range = editor.getSelection(true) || { index: Math.max(0, editor.getLength() - 1) };
    editor.clipboard.dangerouslyPasteHTML(range.index, html, "user");
    setLastFocus("body");
  };

  const brand = appSettings?.brand_color || "#005191";
  const DESIGN_BLOCKS = [
    { key: "header", label: "Header", html: `<h1 class="ql-align-center"><strong style="color:${brand}">Your headline here</strong></h1><p><br/></p>` },
    { key: "greeting", label: "Greeting", html: `<p>Hi {{first_name}},</p><p><br/></p>` },
    { key: "bullets", label: "Bullet list", html: `<ul><li>First key point</li><li>Second key point</li><li>Third key point</li></ul><p><br/></p>` },
    { key: "signature", label: "Signature", html: `<p>Best regards,</p><p><strong>${appSettings?.from_name || "The team"}</strong></p>` },
  ];

  const insertButton = () => {
    if (!btnLabel.trim() || !btnUrl.trim() || btnUrl.trim() === "https://") { toast.error("Give the button a label and a link"); return; }
    insertBlock(`<p class="ql-align-center"><a href="${btnUrl.trim()}" style="background-color:${brand};color:#ffffff;font-weight:bold">${btnLabel.trim()}</a></p><p><br/></p>`);
    setBtnDialogOpen(false);
  };

  if (!c) return <div className="text-sm text-zinc-500">Loading…</div>;

  const update = (patch) => setC({...c, ...patch});

  const save = async (silent = false) => {
    const updated = await api.updateCampaign(id, {
      name: c.name, subject: c.subject, preheader: c.preheader || "", html: c.html,
      editor_mode: c.editor_mode || "richtext", blocks: c.blocks || [],
      audience_type: c.audience_type, audience_id: c.audience_id,
      audience_filter: c.audience_filter || null,
      ab_enabled: !!c.ab_enabled, subject_b: c.subject_b || null,
      ab_test_pct: c.ab_test_pct || 10, ab_wait_minutes: c.ab_wait_minutes || 240,
      from_name: c.from_name, from_email: c.from_email,
      reply_to: c.reply_to || "",
      scheduled_at: c.scheduled_at,
      topic: c.topic || null, recurring: c.recurring || "none",
    });
    setC(updated);
    if (!silent) toast.success("Saved");
    return updated;
  };

  const goToStep = async (n) => {
    if (editable) { try { await save(true); } catch { /* keep navigating */ } }
    setStep(n);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const sendTest = async () => {
    if (!testEmail) return;
    await save(true);
    try { await api.testSend(id, testEmail); toast.success(`Test copy sent to ${testEmail} — check that inbox`); }
    catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
  };

  const sendToSeeds = async () => {
    await save(true);
    try {
      const r = await api.testSendSeeds(id);
      toast.success(`Sent to ${r.sent} seed address${r.sent === 1 ? "" : "es"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Seed send failed");
    }
  };

  const sendNow = async () => {
    await save(true);
    setPreflightOpen(true);
  };

  const stopSending = async () => {
    if (!stopArmed) {
      setStopArmed(true);
      setTimeout(() => setStopArmed(false), 4000);
      return;
    }
    try {
      await api.stopCampaign(id);
      toast.success("Sending stopped");
      setStopArmed(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Stop failed");
    }
  };

  const confirmSend = async () => {
    setPreflightOpen(false);
    try {
      await api.sendCampaign(id);
      toast.success("Sending started");
      load();
      setTab("analytics");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
  };

  const aiSubjects = async () => {
    const bodyText = c.editor_mode === "blocks"
      ? (c.blocks || []).map(b => `${b.headline || ""} ${b.subtext || ""} ${b.text || ""} ${(b.html || "").replace(/<[^>]+>/g, " ")}`).join(" ")
      : (c.html || "").replace(/<[^>]+>/g, " ");
    if (!bodyText.trim()) { toast.error("Write the email content first — the AI uses it to suggest subjects"); return; }
    setAiBusy(true);
    try { const r = await api.aiSubject(bodyText, "professional", 3); toast.success("Suggestions ready");
      if (r.suggestions?.length) update({ subject: r.suggestions[0] });
      window.alert("Suggestions:\n- " + (r.suggestions || []).join("\n- "));
    } finally { setAiBusy(false); }
  };

  const aiDraft = async () => {
    const goal = window.prompt("What's the goal of this email? (e.g. \"invite pharma clients to a MediHUB demo\")");
    if (!goal) return;
    setAiBusy(true);
    try {
      if (c.editor_mode === "blocks") {
        const r = await api.aiDesign(goal, "professional", "");
        update({ subject: r.subject || c.subject, blocks: r.blocks || c.blocks });
        toast.success("Designed email generated — tweak any block below");
      } else {
        const r = await api.aiDraft(goal, "professional", "");
        update({ subject: r.subject || c.subject, html: r.html || c.html });
        toast.success("Draft generated");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI generation failed");
    } finally { setAiBusy(false); }
  };

  const applyTemplate = (t) => {
    if ((t.editor_mode || "richtext") === "blocks") update({ subject: t.subject || c.subject, editor_mode: "blocks", blocks: t.blocks || [] });
    else update({ subject: t.subject, editor_mode: "richtext", html: t.html });
    toast.success(`Loaded ${t.name}`);
  };

  const saveAsTemplate = async () => {
    const name = window.prompt("Save this design as a template. Template name:", c.name);
    if (!name) return;
    await api.createTemplate({
      name, subject: c.subject || "",
      html: isBlocks ? "" : (c.html || ""),
      editor_mode: c.editor_mode || "richtext",
      blocks: isBlocks ? (c.blocks || []) : [],
      description: "Saved from campaign",
    });
    toast.success("Design saved to Templates — load it in any future campaign");
  };

  const duplicate = async () => {
    const copy = await api.duplicateCampaign(id);
    toast.success("Draft copy created");
    window.location.href = `/campaigns/${copy.id}`;
  };

  const toggleRecurring = async () => {
    const next = (c.recurring || "none") === "monthly" ? "none" : "monthly";
    const updated = await api.updateCampaign(id, { recurring: next });
    setC(updated);
    toast.success(next === "monthly" ? "Repeat on — a fresh draft copy will appear on the 1st of every month" : "Monthly repeat turned off");
  };

  const downloadReport = async () => {
    try {
      const blob = await api.campaignReport(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(c.name || "campaign").replace(/[^a-z0-9]+/gi, "-")}-report.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate the report"); }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        try { await api.uploadAttachment(id, f); }
        catch (err) { toast.error(`${f.name}: ${err.response?.data?.detail || "upload failed"}`); }
      }
      await loadAttachments();
      toast.success("Attachment(s) added");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const removeAttachment = async (aid) => {
    await api.deleteAttachment(id, aid);
    await loadAttachments();
    toast.success("Removed");
  };

  const fmtBytes = (n) => n < 1024 ? `${n} B` : n < 1024*1024 ? `${(n/1024).toFixed(1)} KB` : `${(n/(1024*1024)).toFixed(2)} MB`;

  // analytics computed
  const stats = c.stats || {};
  const sent = stats.sent || 0;
  const openRate = sent ? ((stats.unique_opens / sent) * 100) : 0;
  const clickRate = sent ? ((stats.unique_clicks / sent) * 100) : 0;
  const bounceRate = sent ? ((stats.bounced / sent) * 100) : 0;
  const funnelData = [
    { name: "Sent", value: sent },
    { name: "Delivered", value: stats.delivered || 0 },
    { name: "Opens", value: stats.unique_opens || 0 },
    { name: "Clicks", value: stats.unique_clicks || 0 },
  ];
  const COLORS = ["#09090b", "#3f3f46", "#10b981", "#7c3aed"];

  const editable = c.status === "draft" || c.status === "scheduled" || c.status === "failed";
  const isBlocks = c.editor_mode === "blocks";
  const hasContent = isBlocks ? (c.blocks || []).length > 0 : !!(c.html || "").replace(/<[^>]+>/g, "").trim();
  const replyToAddrs = (c.reply_to || "").split(",").map(a => a.trim()).filter(Boolean);
  const replyToOk = replyToAddrs.length > 0 && replyToAddrs.every(a => a.includes("@"));
  const canSend = editable && !!c.subject && hasContent && (c.stats.recipients || 0) > 0 && replyToOk && (!c.ab_enabled || !!(c.subject_b || "").trim());

  const designDone = !!c.subject && hasContent;
  const audienceDone = (c.stats.recipients || 0) > 0;
  const STEPS = [
    { n: 1, icon: PencilSimple, title: "Design", desc: "Subject + email content", done: designDone },
    { n: 2, icon: UsersThree, title: "Audience & sender", desc: "Who gets it, who it's from", done: audienceDone },
    { n: 3, icon: RocketLaunch, title: "Review & send", desc: "Test, approve, launch", done: c.status === "sent" },
  ];

  const fromName = c.from_name || appSettings?.from_name || "—";
  const fromEmail = c.from_email || appSettings?.from_email || "—";

  return (
    <div className="space-y-5">
      <Link to="/campaigns" className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-950"><ArrowLeft size={14}/> Campaigns</Link>

      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <input data-testid="campaign-name" value={c.name} onChange={(e) => update({ name: e.target.value })}
            onBlur={async (e) => {
              if (!editable) {
                const v = e.target.value.trim();
                if (!v) { toast.error("Name cannot be empty"); return; }
                try { await api.updateCampaign(id, { name: v }); toast.success("Campaign renamed"); }
                catch { toast.error("Rename failed"); }
              }
            }}
            title={editable ? undefined : "Click to rename — stats and history stay attached"}
            className="font-heading text-3xl sm:text-4xl font-medium tracking-tight bg-transparent border-0 focus:outline-none w-full"/>
          <div className="flex items-center gap-2 mt-1">
            <StatusBadge value={c.status}/>
            <span className="text-xs text-zinc-500">{c.stats.recipients?.toLocaleString() || 0} recipients</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {c.status === "sending" && (
            <button data-testid="campaign-stop" onClick={stopSending} className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border ${stopArmed ? "bg-rose-600 border-rose-600 text-white hover:bg-rose-700" : "border-rose-200 text-rose-700 hover:bg-rose-50"}`}>
              <X size={14}/> {stopArmed ? "Click again to confirm stop" : "Stop sending"}
            </button>
          )}
          {c.status === "sent" && (
            <>
              <button data-testid="campaign-recurring" onClick={toggleRecurring} title="Automatically recreate this campaign as a fresh draft on the 1st of every month" className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border ${(c.recurring || "none") === "monthly" ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-zinc-200 hover:bg-zinc-50"}`}>
                <Clock size={14}/> {(c.recurring || "none") === "monthly" ? "Repeats monthly" : "Repeat monthly"}
              </button>
              <button data-testid="campaign-followup" onClick={() => setFollowUpOpen(true)} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50">
                <ArrowBendUpLeft size={14}/> Send follow-up
              </button>
            </>
          )}
          <button data-testid="campaign-duplicate" onClick={duplicate} title="Create an editable draft copy of this campaign" className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50">
            <Copy size={14}/> Duplicate
          </button>
          {editable && (
            <>
              <button data-testid="campaign-save" onClick={() => save()} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50"><FloppyDisk size={14}/> Save draft</button>
              <button data-testid="campaign-send" disabled={!canSend} onClick={() => { setStep(3); sendNow(); }} title={canSend ? "Opens the pre-send checklist" : "Finish the subject, content and audience first"} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-50"><PaperPlaneTilt size={14}/> Review & send</button>
            </>
          )}
        </div>
      </div>

      <div className="border-b border-zinc-200 flex gap-1">
        {["compose", "analytics"].map(tk => (
          <button key={tk} onClick={() => { setTab(tk); if (tk==="analytics") { loadRecipients(); loadHealth(); } }} className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${tab===tk ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-900"}`} data-testid={`tab-${tk}`}>
            {tk === "compose" ? "Compose" : "Analytics"}
          </button>
        ))}
      </div>

      {tab === "compose" ? (
        <div className="space-y-5">
          {/* ===== Stepper ===== */}
          <div className="grid grid-cols-3 gap-2" data-testid="campaign-stepper">
            {STEPS.map((s) => (
              <button
                key={s.n}
                data-testid={`step-${s.n}`}
                onClick={() => goToStep(s.n)}
                className={`flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${
                  step === s.n ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 bg-white hover:border-zinc-400"}`}
              >
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${step === s.n ? "bg-white/15" : s.done ? "bg-emerald-50" : "bg-zinc-100"}`}>
                  {s.done && step !== s.n
                    ? <CheckCircle size={17} weight="fill" className="text-emerald-600"/>
                    : <s.icon size={16} className={step === s.n ? "text-white" : "text-zinc-500"}/>}
                </span>
                <span className="min-w-0">
                  <span className={`block text-sm font-medium ${step === s.n ? "text-white" : "text-zinc-900"}`}>{s.n}. {s.title}</span>
                  <span className={`block text-[11px] truncate ${step === s.n ? "text-zinc-300" : "text-zinc-500"}`}>{s.desc}</span>
                </span>
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* LEFT — step content */}
            <div className="space-y-4">
              {/* ===================== STEP 1 — DESIGN ===================== */}
              {step === 1 && (
                <>
                  <div className="rounded-lg border border-zinc-200 bg-white p-4">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">Subject line</div>
                    <p className="text-[11px] text-zinc-500 mb-2">The first thing recipients see in their inbox. The AI button suggests subjects based on your email content.</p>
                    <div className="flex gap-2">
                      <input ref={subjectRef} onFocus={() => setLastFocus("subject")} data-testid="cmp-subject" disabled={!editable} value={c.subject} onChange={(e) => update({ subject: e.target.value })} placeholder="e.g. {{first_name}}, meet MediHUB" className="h-9 flex-1 rounded-md border border-zinc-200 px-3 text-sm"/>
                      <button onClick={aiSubjects} disabled={aiBusy || !editable} title="AI suggests 3 subject lines from your email content" className="inline-flex items-center gap-1 text-xs px-2.5 rounded-md border border-zinc-200 hover:bg-zinc-50"><Sparkle size={12}/> Suggest</button>
                    </div>
                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">Preview text (preheader)</div>
                        <span data-testid="preheader-count" className={`text-[11px] ${(c.preheader || "").length > 90 ? "text-amber-600" : "text-zinc-400"}`}>{(c.preheader || "").length}/90</span>
                      </div>
                      <input data-testid="cmp-preheader" disabled={!editable} value={c.preheader || ""} onChange={(e) => update({ preheader: e.target.value })} placeholder="e.g. Our new Sittingbourne office address — please update your records." className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm"/>
                      <p className="text-[11px] text-zinc-500 mt-1">The snippet shown next to the subject in the inbox — invisible inside the opened email. Aim for 40–90 characters that add to the subject rather than repeat it. Leave blank to let the inbox use the first line of your email.</p>
                    </div>
                    {c.audience_type === "followup" && (c.subject || "").trim().toLowerCase() === (c.followup_source_subject || "").trim().toLowerCase() && (
                      <p data-testid="followup-subject-warning" className="text-xs text-rose-600 mt-2">This is a follow-up: the subject must be different from the original ("{c.followup_source_subject}") before it can be sent.</p>
                    )}
                  </div>

                  <div className="rounded-lg border border-zinc-200 bg-white p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">A/B subject test</div>
                        <p className="text-[11px] text-zinc-500 mt-0.5">Try two subject lines on a small slice of the audience — the one with the better open rate is automatically sent to everyone else.</p>
                      </div>
                      <button data-testid="ab-toggle" disabled={!editable} onClick={() => update({ ab_enabled: !c.ab_enabled })}
                        className={`relative shrink-0 ml-3 h-6 w-11 rounded-full transition-colors ${c.ab_enabled ? "bg-zinc-950" : "bg-zinc-200"}`}>
                        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${c.ab_enabled ? "translate-x-[22px]" : "translate-x-0.5"}`}/>
                      </button>
                    </div>
                    {c.ab_enabled && (
                      <div className="mt-3 space-y-2" data-testid="ab-settings">
                        <div>
                          <span className="block text-[10px] font-medium text-zinc-500 uppercase tracking-wider mb-1">Subject B (the challenger)</span>
                          <input data-testid="ab-subject-b" disabled={!editable} value={c.subject_b || ""} onChange={(e) => update({ subject_b: e.target.value })} placeholder="A different angle on the same email…" className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm"/>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <span className="block text-[10px] font-medium text-zinc-500 uppercase tracking-wider mb-1">Test size (each variant)</span>
                            <select data-testid="ab-test-pct" disabled={!editable} value={c.ab_test_pct || 10} onChange={(e) => update({ ab_test_pct: Number(e.target.value) })} className="h-9 w-full rounded-md border border-zinc-200 px-2 text-sm">
                              <option value={10}>10% + 10%</option>
                              <option value={15}>15% + 15%</option>
                              <option value={20}>20% + 20%</option>
                            </select>
                          </div>
                          <div>
                            <span className="block text-[10px] font-medium text-zinc-500 uppercase tracking-wider mb-1">Decision window</span>
                            <select data-testid="ab-wait" disabled={!editable} value={c.ab_wait_minutes || 240} onChange={(e) => update({ ab_wait_minutes: Number(e.target.value) })} className="h-9 w-full rounded-md border border-zinc-200 px-2 text-sm">
                              <option value={60}>1 hour</option>
                              <option value={120}>2 hours</option>
                              <option value={240}>4 hours</option>
                              <option value={480}>8 hours</option>
                              <option value={1440}>24 hours</option>
                            </select>
                          </div>
                        </div>
                        <p className="text-[11px] text-zinc-500">Winner is decided by <strong>unique open rate</strong>, then sent to the remaining {100 - 2 * (c.ab_test_pct || 10)}% automatically. One approval covers both variants.</p>
                      </div>
                    )}
                  </div>

                  <div className="rounded-lg border border-zinc-200 bg-white p-4">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">{isBlocks ? "Email design" : "Email body"}</div>
                      <div className="flex gap-1">
                        <select data-testid="tpl-load" disabled={!editable} onChange={(e) => { const t = templates.find(t => t.id === e.target.value); if (t) applyTemplate(t); e.target.value = ""; }} className="h-7 rounded-md border border-zinc-200 px-2 text-xs">
                          <option value="">Load template…</option>
                          {templates.map(t => <option key={t.id} value={t.id}>{t.name}{(t.editor_mode || "richtext") === "blocks" ? " (design)" : ""}</option>)}
                        </select>
                        <button data-testid="save-as-template" onClick={saveAsTemplate} disabled={!c.subject && !hasContent} title="Save this subject + design as a reusable template" className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-zinc-200 hover:bg-zinc-50 disabled:opacity-40"><FloppyDisk size={12}/> Save as template</button>
                        <button data-testid="ai-draft-btn" onClick={aiDraft} disabled={aiBusy || !editable} title="Describe the email's goal and the AI generates a complete draft you can edit" className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-zinc-200 hover:bg-zinc-50"><Sparkle size={12}/> {isBlocks ? "Generate with AI" : "Draft with AI"}</button>
                      </div>
                    </div>
                    <p className="text-[11px] text-zinc-500 mb-3">{isBlocks ? "Stack blocks to build the email — reorder with the arrows, duplicate or delete from each block's header. The preview on the right always shows the exact final email." : "Write the email — the preview on the right always shows the exact final email."}</p>
                    {isBlocks ? (
                      <EmailBuilder blocks={c.blocks || []} onChange={(b) => update({ blocks: b })} settings={appSettings} disabled={!editable}/>
                    ) : (
                    <div data-testid="cmp-editor" onFocus={() => setLastFocus("body")}>
                      <ReactQuill ref={quillRef} readOnly={!editable} theme="snow" value={c.html} onChange={(v) => update({ html: v })}/>
                    </div>
                    )}
                  </div>

                  <div className="rounded-lg border border-zinc-200 bg-white p-4">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-2">Personalisation fields</div>
                    <div className="flex flex-wrap gap-1.5" data-testid="merge-fields">
                      {MERGE_FIELDS.map(f => (
                        <button key={f.key} data-testid={`merge-field-${f.key}`} disabled={!editable} onMouseDown={(e) => e.preventDefault()} onClick={() => insertField(f.key)}
                          className="text-xs px-2.5 py-1 rounded-full border border-zinc-200 bg-zinc-50 hover:bg-zinc-950 hover:text-white hover:border-zinc-950 transition-colors font-mono disabled:opacity-40">
                          {f.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] text-zinc-500 mt-2">{isBlocks ? "Click to copy a tag, then paste it into any text field. At send time each recipient sees their own data." : "Click to insert at your cursor — works in the subject and the body. At send time each recipient sees their own data."}</p>
                    {!isBlocks && (<>
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mt-3 mb-2 pt-3 border-t border-zinc-100">Design blocks</div>
                    <div className="flex flex-wrap gap-1.5" data-testid="design-blocks">
                      {DESIGN_BLOCKS.map(b => (
                        <button key={b.key} data-testid={`block-${b.key}`} disabled={!editable} onMouseDown={(e) => e.preventDefault()} onClick={() => insertBlock(b.html)}
                          className="text-xs px-2.5 py-1 rounded-md border border-zinc-200 bg-white hover:bg-zinc-50 disabled:opacity-40">
                          {b.label}
                        </button>
                      ))}
                      <button data-testid="block-button" disabled={!editable} onClick={() => setBtnDialogOpen(true)} className="text-xs px-2.5 py-1 rounded-md border text-white disabled:opacity-40" style={{ backgroundColor: brand, borderColor: brand }}>
                        CTA button
                      </button>
                    </div>
                    <p className="text-[11px] text-zinc-500 mt-2">One-click branded pieces — the CTA button renders as a real button in the sent email. Brand colour lives in Settings.</p>
                    </>)}
                  </div>

                  <div className="flex justify-end">
                    <button data-testid="step-next-1" onClick={() => goToStep(2)} className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-md bg-zinc-950 text-white hover:bg-zinc-800">
                      Continue to audience <ArrowRight size={14}/>
                    </button>
                  </div>
                </>
              )}

              {/* ===================== STEP 2 — AUDIENCE & SENDER ===================== */}
              {step === 2 && (
                <>
                  <div className="rounded-lg border border-zinc-200 bg-white p-4">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">Who receives this email</div>
                    {c.audience_type === "followup" ? (
                      <div data-testid="followup-audience-card" className="rounded-md border border-indigo-200 bg-indigo-50/60 px-3 py-2.5">
                        <div className="text-sm font-medium text-zinc-900">Follow-up of "{c.followup_source_name || "original campaign"}"</div>
                        <p className="text-[11px] text-zinc-600 mt-0.5">Targeting recipients who <strong>{FOLLOWUP_LABELS[c.followup_criteria] || "didn't open"}</strong>. The list is re-checked at send time — anyone who engages with the original before then is dropped automatically. Bounced, unsubscribed and suppressed contacts are excluded.</p>
                        {editable && (
                          <button data-testid="followup-switch-audience" onClick={() => update({ audience_type: "list", audience_id: "", followup_criteria: null })} className="mt-2 text-[11px] text-zinc-600 underline hover:text-zinc-900">Switch to a normal audience instead</button>
                        )}
                      </div>
                    ) : (
                    <>
                    <p className="text-[11px] text-zinc-500 mb-2">Start with everyone, a static list, or a smart segment — the count below updates as you pick.</p>
                    <div className="flex gap-2 items-center">
                      <select data-testid="aud-type" disabled={!editable} value={c.audience_type} onChange={(e) => update({ audience_type: e.target.value, audience_id: "", audience_filter: e.target.value === "segment" ? null : c.audience_filter })} className="h-9 rounded-md border border-zinc-200 px-2 text-sm">
                        <option value="all">All active contacts</option>
                        <option value="list">List</option>
                        <option value="segment">Segment</option>
                      </select>
                      {c.audience_type === "list" && (
                        <select data-testid="aud-list" disabled={!editable} value={c.audience_id || ""} onChange={(e) => update({ audience_id: e.target.value })} className="h-9 flex-1 rounded-md border border-zinc-200 px-2 text-sm">
                          <option value="">— select list —</option>
                          {lists.map(l => <option key={l.id} value={l.id}>{l.name} ({l.member_count})</option>)}
                        </select>
                      )}
                      {c.audience_type === "segment" && (
                        <select data-testid="aud-seg" disabled={!editable} value={c.audience_id || ""} onChange={(e) => update({ audience_id: e.target.value })} className="h-9 flex-1 rounded-md border border-zinc-200 px-2 text-sm">
                          <option value="">— select segment —</option>
                          {segments.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      )}
                    </div>
                    {(c.audience_type === "all" || c.audience_type === "list") && (
                      <div className="mt-3">
                        <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">Narrow it down <span className="normal-case font-normal">(optional)</span></div>
                        <p className="text-[11px] text-zinc-500 mb-2">Only contacts in your selection above <strong>who also match this filter</strong> will receive the email.</p>
                        <select data-testid="aud-filter" disabled={!editable} value={c.audience_filter || ""} onChange={(e) => update({ audience_filter: e.target.value || null })} className="h-9 w-full rounded-md border border-zinc-200 px-2 text-sm">
                          <option value="">No filter — everyone in the selection</option>
                          <optgroup label="Quick filters">
                            <option value="engaged_90d">Engaged in the last 90 days</option>
                            <option value="highly_engaged">Highly engaged (score ≥ 10)</option>
                            <option value="never_opened">Never opened an email</option>
                          </optgroup>
                          {segments.length > 0 && (
                            <optgroup label="Saved segments">
                              {segments.map(s => <option key={s.id} value={`segment:${s.id}`}>{s.name}</option>)}
                            </optgroup>
                          )}
                        </select>
                      </div>
                    )}
                    </>
                    )}
                    <div className="mt-3 rounded-md bg-zinc-50 border border-zinc-200 px-3 py-2 text-sm" data-testid="audience-count">
                      Currently targeting <strong data-testid="audience-count-number">{(liveCount ?? c.stats.recipients ?? 0).toLocaleString()}</strong> recipient{(liveCount ?? c.stats.recipients) === 1 ? "" : "s"}
                      {c.audience_filter ? <> — <span className="text-zinc-600">{audienceLabel()}</span></> : ""}. Suppressed and unsubscribed contacts are excluded automatically.
                      {c.group_id && c.audience_type !== "followup" && (
                        <div className="mt-1.5 text-xs flex items-center flex-wrap gap-x-3 gap-y-1" data-testid="folder-dedupe-note">
                          {c.exclude_folder_sent !== false ? (
                            <span className="text-sky-700">
                              {folderSkipped > 0
                                ? `Folder de-duplication: skipping ${folderSkipped.toLocaleString()} contact${folderSkipped === 1 ? "" : "s"} already emailed by this folder's other campaigns.`
                                : "Folder de-duplication is on — anyone already emailed by this folder's other campaigns is skipped automatically."}
                            </span>
                          ) : (
                            <span className="text-amber-700">Folder de-duplication is OFF — earlier recipients in this folder may get this campaign again.</span>
                          )}
                          {editable && (
                            <label className="inline-flex items-center gap-1.5 cursor-pointer text-zinc-600">
                              <input type="checkbox" data-testid="folder-dedupe-toggle" checked={c.exclude_folder_sent !== false} onChange={(e) => update({ exclude_folder_sent: e.target.checked })} />
                              skip previous folder recipients
                            </label>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {(appSettings?.preference_topics || []).length > 0 && (
                    <div className="rounded-lg border border-zinc-200 bg-white p-4">
                      <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">Topic <span className="normal-case font-normal">(optional)</span></div>
                      <p className="text-[11px] text-zinc-500 mb-2">Contacts who opted out of this topic in the preference centre are skipped automatically at send time.</p>
                      <select data-testid="cmp-topic" disabled={!editable} value={c.topic || ""} onChange={(e) => update({ topic: e.target.value || null })} className="h-9 w-full rounded-md border border-zinc-200 px-2 text-sm">
                        <option value="">No topic — send to everyone in the audience</option>
                        {(appSettings.preference_topics || []).map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  )}

                  <div className="rounded-lg border border-zinc-200 bg-white p-4">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">Sender identity</div>
                    <p className="text-[11px] text-zinc-500 mb-2">What recipients see in the "From" line. Leave blank to use your defaults from Settings ({appSettings?.from_name || "…"} &lt;{appSettings?.from_email || "…"}&gt;).</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <span className="block text-[10px] font-medium text-zinc-500 uppercase tracking-wider mb-1">From name</span>
                        <input data-testid="sender-name" disabled={!editable} value={c.from_name || ""} onChange={(e) => update({ from_name: e.target.value })} placeholder={appSettings?.from_name || "Medicys"} className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm"/>
                      </div>
                      <div>
                        <span className="block text-[10px] font-medium text-zinc-500 uppercase tracking-wider mb-1">From email</span>
                        <input data-testid="sender-email" disabled={!editable} value={c.from_email || ""} onChange={(e) => update({ from_email: e.target.value })} placeholder={appSettings?.from_email || "you@yourdomain.com"} className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm"/>
                      </div>
                    </div>
                    <div className="mt-2">
                      <span className="block text-[10px] font-medium text-zinc-500 uppercase tracking-wider mb-1">Replies go to <span className="text-rose-500">(required)</span></span>
                      <input data-testid="campaign-replyto" disabled={!editable} value={c.reply_to || ""} onChange={(e) => update({ reply_to: e.target.value })} placeholder="you@medicysltd.co.uk, colleague@medicysltd.co.uk" className={`h-9 w-full rounded-md border px-3 text-sm ${replyToOk ? "border-zinc-200" : "border-rose-300 bg-rose-50/40"}`}/>
                      <p className="text-[11px] text-zinc-500 mt-1">Who receives answers to this campaign. Several addresses allowed, separated by commas. A campaign cannot send without this.</p>
                    </div>
                  </div>

                  <div className="rounded-lg border border-zinc-200 bg-white p-4">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-1">Images</div>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" data-testid="inline-images-toggle" disabled={!editable} checked={!!c.inline_images} onChange={(e) => update({ inline_images: e.target.checked })} className="mt-0.5"/>
                      <span>
                        <span className="block text-xs font-medium text-zinc-900">Embed images inside the email</span>
                        <span className="block text-[11px] text-zinc-500 mt-0.5">Your logo and pictures travel with the message, so they display even when Outlook blocks remote images ("image removed by the sender"). Best for small images — anything over 400 KB stays hosted. Makes each email slightly larger.</span>
                      </span>
                    </label>
                  </div>

                  <div className="rounded-lg border border-zinc-200 bg-white p-4">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 inline-flex items-center gap-1.5"><Paperclip size={12}/> Attachments</div>
                      {editable && (
                        <label className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md border border-zinc-200 hover:bg-zinc-50 cursor-pointer" data-testid="attach-add">
                          <input type="file" multiple onChange={handleUpload} className="hidden" disabled={uploading} data-testid="attach-input"/>
                          {uploading ? "Uploading…" : "Add file"}
                        </label>
                      )}
                    </div>
                    <p className="text-[11px] text-zinc-500 mb-2">Files attached to every recipient's email. Up to 10 MB per file, 25 MB total.</p>
                    {attachments.length === 0 ? (
                      <div className="text-xs text-zinc-500 py-1">No attachments.</div>
                    ) : (
                      <ul className="space-y-1.5" data-testid="attach-list">
                        {attachments.map(a => (
                          <li key={a.id} className="flex items-center justify-between rounded-md border border-zinc-200 px-2.5 py-1.5 text-sm">
                            <div className="flex items-center gap-2 min-w-0">
                              <Paperclip size={13} className="text-zinc-400 shrink-0"/>
                              <span className="truncate font-medium text-zinc-900">{a.filename}</span>
                              <span className="text-[11px] text-zinc-500 shrink-0">{fmtBytes(a.size)}</span>
                            </div>
                            {editable && (
                              <button onClick={() => removeAttachment(a.id)} className="text-zinc-400 hover:text-rose-700 ml-2" data-testid={`attach-remove-${a.filename}`}><X size={13}/></button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="flex justify-between">
                    <button data-testid="step-back-2" onClick={() => goToStep(1)} className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-md border border-zinc-200 hover:bg-zinc-50">
                      <ArrowLeft size={14}/> Back to design
                    </button>
                    <button data-testid="step-next-2" onClick={() => goToStep(3)} className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-md bg-zinc-950 text-white hover:bg-zinc-800">
                      Continue to review <ArrowRight size={14}/>
                    </button>
                  </div>
                </>
              )}

              {/* ===================== STEP 3 — REVIEW & SEND ===================== */}
              {step === 3 && (
                <>
                  <div className="rounded-lg border border-zinc-200 bg-white p-4">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-2">Campaign summary</div>
                    <div className="divide-y divide-zinc-100" data-testid="review-summary">
                      <SummaryRow label="Subject" value={c.ab_enabled ? `A/B test — A: “${c.subject || "?"}” vs B: “${c.subject_b || "missing"}”` : (c.subject || "Missing")} ok={!!c.subject && (!c.ab_enabled || !!(c.subject_b || "").trim())} onEdit={editable ? () => goToStep(1) : null}/>
                    <SummaryRow label="Preview text" value={(c.preheader || "").trim() || "Not set — inbox will use the first line of the email"} ok={true} onEdit={editable ? () => goToStep(1) : null}/>
                      <SummaryRow label="Content" value={isBlocks ? `${(c.blocks || []).length} design block${(c.blocks || []).length === 1 ? "" : "s"}` : (hasContent ? "Written" : "Empty")} ok={hasContent} onEdit={editable ? () => goToStep(1) : null}/>
                      <SummaryRow label="Audience" value={`${audienceLabel()} — ${(c.stats.recipients || 0).toLocaleString()} recipients`} ok={(c.stats.recipients || 0) > 0} onEdit={editable ? () => goToStep(2) : null}/>
                      <SummaryRow label="From" value={`${fromName} <${fromEmail}>`} ok={fromEmail !== "—"} onEdit={editable ? () => goToStep(2) : null}/>
                      <SummaryRow label="Replies go to" value={replyToOk ? c.reply_to : "Missing — every campaign must set this"} ok={replyToOk} onEdit={editable ? () => goToStep(2) : null}/>
                      <SummaryRow label="Attachments" value={attachments.length ? `${attachments.length} file${attachments.length === 1 ? "" : "s"}` : "None"} ok onEdit={editable ? () => goToStep(2) : null}/>
                    </div>
                  </div>

                  <VerifyRecipientsCard campaignId={id} editable={editable}/>

                  {editable && (
                    <>
                      <div className="rounded-lg border border-zinc-200 bg-white p-4 space-y-2">
                        <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">1 · Check it in a real inbox <span className="normal-case font-normal">(optional but recommended)</span></div>
                        <p className="text-[11px] text-zinc-500">Sends this email only to the address you enter — nothing goes to your audience.</p>
                        <div className="flex gap-2">
                          <input data-testid="test-email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@medicysltd.co.uk" className="h-9 flex-1 rounded-md border border-zinc-200 px-3 text-sm"/>
                          <button data-testid="test-send" onClick={sendTest} disabled={!testEmail} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50"><PaperPlane size={14}/> Email me a test</button>
                        </div>
                        <button data-testid="seed-send" onClick={sendToSeeds} className="w-full inline-flex items-center justify-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50 text-zinc-600">
                          <PaperPlane size={12}/> Also send to my seed list (Gmail/Outlook inbox-placement check — configure in Settings)
                        </button>
                      </div>

                      <div className="rounded-lg border border-zinc-200 bg-white p-4 space-y-2">
                        <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 inline-flex items-center gap-1.5"><Clock size={12}/> 2 · Send now or schedule</div>
                        <p className="text-[11px] text-zinc-500">Leave empty to send immediately when you launch. Scheduled campaigns still require approval — unapproved ones return to draft instead of sending.</p>
                        <input
                          data-testid="schedule-input"
                          type="datetime-local"
                          value={c.scheduled_at ? c.scheduled_at.slice(0, 16) : ""}
                          onChange={(e) => update({ scheduled_at: e.target.value ? new Date(e.target.value).toISOString() : null })}
                          className="h-9 rounded-md border border-zinc-200 px-3 text-sm"
                        />
                        <label className="flex items-start gap-2 text-xs text-zinc-600 pt-1 border-t border-zinc-100 mt-1 cursor-pointer">
                          <input data-testid="recurring-toggle" type="checkbox" checked={(c.recurring || "none") === "monthly"} onChange={(e) => update({ recurring: e.target.checked ? "monthly" : "none" })} className="mt-0.5"/>
                          <span><strong>Repeat monthly</strong> — after this is sent, a fresh draft copy (same design & audience, stats reset) appears in Campaigns on the 1st of every month, ready to update and send.</span>
                        </label>
                      </div>

                      <div className="rounded-lg border-2 border-zinc-950 bg-white p-4 space-y-2">
                        <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">3 · Get approval & launch</div>
                        <p className="text-xs text-zinc-600">This opens the <strong>pre-send checklist</strong>. From there you email the approval copy to {(appSettings?.approval_recipients || []).join(" and ") || "the approvers"}; once {appSettings?.approver_email || "the approver"} clicks Approve in their inbox, you type <span className="font-mono font-semibold">SEND</span> to launch. Nothing is sent to your audience before then.</p>
                        <button data-testid="open-preflight" disabled={!canSend} onClick={sendNow} title={canSend ? "" : "Finish the subject, content and audience first"} className="w-full inline-flex items-center justify-center gap-1.5 text-sm px-4 py-2.5 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-50">
                          <RocketLaunch size={15}/> Open pre-send checklist
                        </button>
                        {!canSend && <p className="text-[11px] text-rose-600">To enable: {[!c.subject && "add a subject", !hasContent && "add email content", (c.stats.recipients || 0) === 0 && "choose an audience with at least 1 recipient", !replyToOk && "set who receives replies (step 2, Sender identity)"].filter(Boolean).join(" · ")}</p>}
                      </div>
                    </>
                  )}

                  <div className="flex justify-start">
                    <button data-testid="step-back-3" onClick={() => goToStep(2)} className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-md border border-zinc-200 hover:bg-zinc-50">
                      <ArrowLeft size={14}/> Back to audience
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* RIGHT — preview */}
            <div className="space-y-3">
              <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 sticky top-20">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">Preview — exactly as it will be sent</div>
                  <span className="text-[10px] text-zinc-400">sample: Alex Friend, Example Co</span>
                </div>
                <div className="rounded-md bg-white border border-zinc-200 overflow-hidden" data-testid="email-preview">
                  <div className="px-5 pt-4">
                    <div className="text-xs text-zinc-500">From: {preview?.from_name || "…"} &lt;{preview?.from_email || "…"}&gt;</div>
                    {preview?.reply_to && <div className="text-xs text-zinc-500">Reply-To: {preview.reply_to}</div>}
                    <div className="text-xs text-zinc-500">To: Alex Friend &lt;alex@example.com&gt;</div>
                    <div className="border-t border-zinc-100 my-3"/>
                    <div className="font-heading font-medium text-base mb-2" data-testid="preview-subject">{preview?.subject || <span className="text-zinc-300">No subject yet</span>}</div>
                  </div>
                  <iframe
                    title="Email preview"
                    data-testid="preview-frame"
                    srcDoc={preview?.html || ""}
                    sandbox=""
                    className="w-full bg-white"
                    style={{ height: 460, border: 0 }}
                  />
                  {attachments.length > 0 && (
                    <div className="px-5 pb-4 pt-2 border-t border-zinc-100">
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Attachments</div>
                      <div className="flex flex-wrap gap-1.5">
                        {attachments.map(a => (
                          <span key={a.id} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-zinc-200 bg-zinc-50 text-[11px]">
                            <Paperclip size={11} className="text-zinc-500"/>{a.filename}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          {(c.status === "sent" || c.status === "sending") && (
            <div className="flex justify-end">
              <button data-testid="board-report-btn" onClick={downloadReport} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50">
                <FilePdf size={15} className="text-rose-600"/> Board report (PDF)
              </button>
            </div>
          )}
          {abStatus && abStatus.phase && (
            <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="ab-results-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-base font-medium">A/B subject test</h3>
                <span className="text-xs text-zinc-500">
                  {abStatus.phase === "done" ? `Winner rolled out ${abStatus.decided_at ? "· decided " + new Date(abStatus.decided_at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}` :
                   abStatus.phase === "testing" ? "Testing in progress" : "Rolling out winner"}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {["a", "b"].map(v => {
                  const d = abStatus[v];
                  const isWinner = abStatus.winner && abStatus.winner.toLowerCase() === v;
                  return (
                    <div key={v} className={`rounded-lg border p-4 ${isWinner ? "border-emerald-300 bg-emerald-50/50" : "border-zinc-200"}`} data-testid={`ab-variant-${v}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">Subject {v.toUpperCase()}</span>
                        {isWinner && <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold" data-testid="ab-winner-badge">WINNER</span>}
                      </div>
                      <p className="text-sm font-medium text-zinc-900 mb-2">“{v === "a" ? abStatus.subject_a : abStatus.subject_b}”</p>
                      <div className="flex gap-4 text-xs text-zinc-600">
                        <span><strong className="text-zinc-900">{d.sent}</strong> sent</span>
                        <span><strong className="text-zinc-900">{d.unique_opens}</strong> unique opens</span>
                        <span><strong className={isWinner ? "text-emerald-700" : "text-zinc-900"}>{d.open_rate}%</strong> open rate</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {health && (
            <div className="rounded-lg border border-zinc-200 bg-white p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-base font-medium">Deliverability health</h3>
                <span className="text-xs text-zinc-500">{health.sent.toLocaleString()} emails sent</span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <CampaignHealthMetric label="Bounce rate" data={health.bounce} suffix={`${health.bounce.count} bounced`} />
                <CampaignHealthMetric label="Spam complaints" data={health.complaint} suffix={`${health.complaint.count} reports`} />
                <CampaignHealthMetric label="Unsubscribe rate" data={health.unsubscribe} suffix={`${health.unsubscribe.count} unsubs`} />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Metric label="Recipients" value={c.stats.recipients || 0}/>
            <Metric label="Sent" value={sent}/>
            <Metric label="Open rate" value={`${openRate.toFixed(1)}%`} sub={`${stats.unique_opens || 0} unique`}/>
            <Metric label="Click rate" value={`${clickRate.toFixed(1)}%`} sub={`${stats.unique_clicks || 0} unique`}/>
            <Metric label="Bounce" value={`${bounceRate.toFixed(1)}%`} sub={`${stats.bounced || 0} hard`}/>
          </div>

          {scanner?.totals && (scanner.totals.opens?.raw > 0 || scanner.totals.clicks?.raw > 0) && (
            <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="stats-reconciliation">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-heading text-base font-medium">Stats you can defend</h3>
                {scanner.totals.hp_hits > 0 && (
                  <span data-testid="hp-hits-badge" className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5">
                    {scanner.totals.hp_hits} honeypot hit{scanner.totals.hp_hits === 1 ? "" : "s"}
                  </span>
                )}
              </div>
              <p className="text-xs text-zinc-500 mb-3">Raw = everything the tracking recorded. Human = after discarding scanner and bot activity. The headline metrics above always use the human numbers.</p>
              <table className="w-full text-sm">
                <thead className="text-xs text-zinc-500"><tr>
                  <th className="text-left py-1.5 font-medium">Metric</th>
                  <th className="text-right py-1.5 font-medium">Raw recorded</th>
                  <th className="text-right py-1.5 font-medium">Human (reported)</th>
                  <th className="text-right py-1.5 font-medium">Discarded</th>
                </tr></thead>
                <tbody>
                  {[["Opens", scanner.totals.opens], ["Unique opens", scanner.totals.unique_opens],
                    ["Clicks", scanner.totals.clicks], ["Unique clicks", scanner.totals.unique_clicks]].map(([label, t]) => (
                    <tr key={label} className="border-t border-zinc-100" data-testid={`recon-row-${label.toLowerCase().replace(" ", "-")}`}>
                      <td className="py-1.5">{label}</td>
                      <td className="py-1.5 text-right text-zinc-500">{(t?.raw ?? 0).toLocaleString()}</td>
                      <td className="py-1.5 text-right font-medium">{(t?.human ?? 0).toLocaleString()}</td>
                      <td className={`py-1.5 text-right ${(t?.raw ?? 0) - (t?.human ?? 0) > 0 ? "text-amber-700" : "text-zinc-400"}`}>{((t?.raw ?? 0) - (t?.human ?? 0)).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-lg border border-zinc-200 bg-white p-5">
              <h3 className="font-heading text-base font-medium mb-3">Funnel</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={funnelData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid stroke="#f4f4f5" vertical={false}/>
                  <XAxis dataKey="name" tick={{fontSize: 11, fill: "#71717a"}} axisLine={false} tickLine={false}/>
                  <YAxis tick={{fontSize: 11, fill: "#71717a"}} axisLine={false} tickLine={false}/>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e4e4e7" }}/>
                  <Bar dataKey="value" radius={[6,6,0,0]}>
                    {funnelData.map((_, i) => <Cell key={i} fill={COLORS[i]}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="rounded-lg border border-zinc-200 bg-white p-5">
              <h3 className="font-heading text-base font-medium mb-3">Status mix</h3>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie dataKey="value" data={[
                    { name: "Opened", value: stats.unique_opens || 0 },
                    { name: "Clicked", value: stats.unique_clicks || 0 },
                    { name: "Bounced", value: stats.bounced || 0 },
                    { name: "Unsubscribed", value: stats.unsubscribed || 0 },
                  ].filter(d => d.value > 0)} innerRadius={50} outerRadius={80}>
                    {COLORS.map((c, i) => <Cell key={i} fill={c}/>)}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e4e4e7" }}/>
                  <Legend wrapperStyle={{ fontSize: 11 }}/>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between gap-3">
              <span className="font-medium text-sm">Recipient activity</span>
              {scanner && (scanner.opens > 0 || scanner.clicks > 0) && (
                <button data-testid="bot-filtered-note" onClick={() => setScannerOpen(v => !v)}
                  className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5 hover:border-amber-400">
                  {scanner.opens} open{scanner.opens === 1 ? "" : "s"} · {scanner.clicks} click{scanner.clicks === 1 ? "" : "s"} discarded as security scanners {scannerOpen ? "▴" : "▾"}
                </button>
              )}
            </div>
            {scannerOpen && scanner && (
              <div className="px-4 py-3 border-b border-zinc-200 bg-amber-50/40" data-testid="scanner-details">
                <p className="text-xs text-zinc-600 mb-2">These were removed from your open/click stats so the numbers reflect real human readers. Why each was discarded:</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {Object.entries(scanner.reasons || {}).map(([r, n]) => (
                    <span key={r} className="text-[11px] px-2 py-1 rounded-md bg-white border border-amber-200 text-zinc-700">
                      {({ "too-fast": "Opened within 30s of delivery", "scanner-ua": "Security-scanner user agent", "bounced-mailbox": "Bounced mailbox (can't be human)", "click-burst": "Rapid repeat clicks (link scanner)", "honeypot": "Hit the invisible trap link (machine)", "apple-privacy": "Apple privacy prefetch (machine open)", "datacenter-ip": "Data-centre IP (cloud server, not a person)", "scanner-ip": "Known security-vendor IP" }[r]) || r}:
                      <strong className="ml-1">{n.opens} open{n.opens === 1 ? "" : "s"}{n.clicks ? ` · ${n.clicks} click${n.clicks === 1 ? "" : "s"}` : ""}</strong>
                    </span>
                  ))}
                </div>
                {(scanner.recent || []).length > 0 && (
                  <table className="w-full text-xs">
                    <thead className="text-[10px] uppercase text-zinc-400"><tr>
                      <th className="text-left py-1 font-medium">Most recent discarded</th>
                      <th className="text-left py-1 font-medium">Type</th>
                      <th className="text-left py-1 font-medium">Reason</th>
                      <th className="text-left py-1 font-medium">When</th>
                    </tr></thead>
                    <tbody>
                      {scanner.recent.map((e, i) => (
                        <tr key={i} className="border-t border-amber-100">
                          <td className="py-1 font-mono text-[11px]">{e.email || e.contact_id}</td>
                          <td className="py-1">{e.type}</td>
                          <td className="py-1">{e.bot_reason || "—"}</td>
                          <td className="py-1 text-zinc-500">{e.created_at ? new Date(e.created_at).toLocaleString() : ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
            <table className="w-full text-sm">
              <thead className="text-xs text-zinc-500 border-b border-zinc-200"><tr>
                <th className="text-left px-3 py-2 font-medium">Contact</th>
                <th className="text-left px-3 py-2 font-medium">Email</th>
                <th className="text-center px-3 py-2 font-medium">Sent</th>
                <th className="text-center px-3 py-2 font-medium">Delivered</th>
                <th className="text-center px-3 py-2 font-medium">Opened</th>
                <th className="text-center px-3 py-2 font-medium">Clicked</th>
                <th className="text-left px-3 py-2 font-medium">Last event</th>
              </tr></thead>
              <tbody>
                {recipients.map(r => <RecipientRow key={r.contact_id} r={r} />)}
                {recipients.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-sm text-zinc-500">No activity yet</td></tr>}
              </tbody>
            </table>
            <p className="px-4 py-2.5 text-[11px] text-zinc-500 border-t border-zinc-100">
              Sorted from most to least engaged (clicks first, then opens). Click a row to see that person's full activity.
              Opens rely on a tracking pixel: they under-count when images are blocked and are best read as a trend, not an exact tally. Clicks are reliable. The Delivered column fills in once the Resend webhook is configured (see Settings).
            </p>
          </div>
        </div>
      )}

      {followUpOpen && <FollowUpDialog campaign={c} onClose={() => setFollowUpOpen(false)} />}
      {preflightOpen && (
        <PreflightModal campaignId={id} onClose={() => setPreflightOpen(false)} onConfirm={confirmSend} />
      )}

      {btnDialogOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setBtnDialogOpen(false)} data-testid="btn-dialog-overlay">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-zinc-200 shadow-xl w-full max-w-sm p-5 space-y-3">
            <h3 className="font-heading text-base font-medium">Insert CTA button</h3>
            <label className="block">
              <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Button label</span>
              <input data-testid="btn-label" value={btnLabel} onChange={(e) => setBtnLabel(e.target.value)} className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm"/>
            </label>
            <label className="block">
              <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Link URL</span>
              <input data-testid="btn-url" value={btnUrl} onChange={(e) => setBtnUrl(e.target.value)} placeholder="https://..." className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm font-mono text-[12.5px]"/>
            </label>
            <div className="flex items-center justify-between pt-1">
              <span className="text-xs px-3 py-1.5 rounded-md text-white font-semibold" style={{ backgroundColor: brand }}>{btnLabel || "Preview"}</span>
              <div className="flex gap-2">
                <button onClick={() => setBtnDialogOpen(false)} className="px-3 py-1.5 text-sm rounded-md border border-zinc-200 hover:bg-zinc-50" data-testid="btn-cancel">Cancel</button>
                <button onClick={insertButton} className="px-3 py-1.5 text-sm rounded-md bg-zinc-950 text-white hover:bg-zinc-800" data-testid="btn-insert">Insert</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const MERGE_FIELDS = [
  { key: "first_name", label: "{{first_name}}" },
  { key: "last_name", label: "{{last_name}}" },
  { key: "email", label: "{{email}}" },
  { key: "company", label: "{{company}}" },
  { key: "job_title", label: "{{job_title}}" },
  { key: "phone", label: "{{phone}}" },
];

const REASON_LABELS = { "too-fast": "opened <30s after delivery", "scanner-ua": "scanner user agent", "bounced-mailbox": "bounced mailbox", "click-burst": "rapid repeat clicks", "honeypot": "honeypot trap", "apple-privacy": "Apple privacy prefetch", "datacenter-ip": "data-centre IP", "scanner-ip": "security-vendor IP" };

function RecipientRow({ r }) {
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
  return (
    <>
      <tr className="border-b border-zinc-100 hover:bg-zinc-50/60 cursor-pointer" onClick={() => setOpen(!open)} data-testid={`recipient-row-${r.contact_id}`}>
        <td className="px-3 py-2">
          <span className="inline-flex items-center gap-1">
            {open ? <CaretDown size={12} className="text-zinc-400 shrink-0"/> : <CaretRight size={12} className="text-zinc-400 shrink-0"/>}
            {r.contact ? `${r.contact.first_name} ${r.contact.last_name}` : r.contact_id}
          </span>
          {r.clicks_count >= 2 && (
            <span data-testid={`hot-lead-badge-${r.contact_id}`} title="Hot lead — clicked 2+ times in this campaign; follow up today"
              className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-semibold text-orange-700 border border-orange-200 bg-orange-50 rounded px-1 py-px align-middle">
              <Fire size={10} weight="fill" /> hot lead
            </span>
          )}
          {r.bot_activity && <span title="Some opens/clicks from this recipient came from a security scanner and were discounted" className="ml-1.5 text-[10px] text-amber-600 border border-amber-200 bg-amber-50 rounded px-1 py-px align-middle">scanner</span>}
          {r.bounced && <span className="ml-1.5 text-[10px] text-rose-600 border border-rose-200 bg-rose-50 rounded px-1 py-px align-middle">bounced</span>}
          {r.unsubscribed && <span data-testid="unsubscribed-badge" className="ml-1.5 text-[10px] text-zinc-600 border border-zinc-300 bg-zinc-100 rounded px-1 py-px align-middle">unsubscribed</span>}
        </td>
        <td className="px-3 py-2 font-mono text-[12.5px] text-zinc-700">{r.contact?.email}</td>
        <td className="px-3 py-2 text-center">{r.sent ? "✓" : ""}</td>
        <td className="px-3 py-2 text-center" title={r.delivered ? "Confirmed delivered by the mailbox provider" : "Needs the Resend webhook to be configured"}>{r.delivered ? "✓" : ""}</td>
        <td className="px-3 py-2 text-center">{r.opened ? (r.opens_count > 1 ? `✓ ×${r.opens_count}` : "✓") : ""}</td>
        <td className="px-3 py-2 text-center">{r.clicked ? (r.clicks_count > 1 ? `✓ ×${r.clicks_count}` : "✓") : ""}</td>
        <td className="px-3 py-2 text-xs text-zinc-500">{r.last_event_at ? new Date(r.last_event_at).toLocaleString() : ""}</td>
      </tr>
      {open && (
        <tr className="border-b border-zinc-100 bg-zinc-50/50" data-testid={`recipient-detail-${r.contact_id}`}>
          <td colSpan={7} className="px-4 py-3">
            <div className="flex flex-wrap gap-2 mb-2">
              <span className="text-[11px] px-2 py-1 rounded-md bg-white border border-zinc-200 text-zinc-700">Opens <strong>{r.opens_count}</strong>{r.inferred_open ? " (incl. inferred from click)" : ""}</span>
              <span className="text-[11px] px-2 py-1 rounded-md bg-white border border-zinc-200 text-zinc-700">Clicks <strong>{r.clicks_count}</strong></span>
              {r.first_open_at && <span className="text-[11px] px-2 py-1 rounded-md bg-white border border-zinc-200 text-zinc-700">First open <strong>{fmt(r.first_open_at)}</strong></span>}
              {r.first_click_at && <span className="text-[11px] px-2 py-1 rounded-md bg-white border border-zinc-200 text-zinc-700">First click <strong>{fmt(r.first_click_at)}</strong></span>}
              {r.contact && <span className="text-[11px] px-2 py-1 rounded-md bg-white border border-zinc-200 text-zinc-700">Overall engagement score <strong>{r.contact.engagement_score ?? 0}</strong></span>}
              {r.contact && (
                <button onClick={(e) => { e.stopPropagation(); setProfileOpen(true); }}
                  className="text-[11px] px-2 py-1 rounded-md border border-sky-200 text-sky-700 hover:bg-sky-50"
                  data-testid={`recipient-open-contact-${r.contact_id}`}>
                  View profile — add tags & notes
                </button>
              )}
            </div>
            {r.clicked_urls?.length > 0 && (
              <div className="text-xs text-zinc-600 mb-2" data-testid={`recipient-links-${r.contact_id}`}>
                <span className="text-zinc-400">Links clicked:</span>{" "}
                {r.clicked_urls.map(u => <span key={u} className="inline-block mr-2 truncate max-w-[340px] align-bottom text-sky-700">{u.replace(/^https?:\/\//, "")}</span>)}
              </div>
            )}
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase text-zinc-400"><tr>
                <th className="text-left py-1 font-medium w-32">Event</th>
                <th className="text-left py-1 font-medium">When</th>
                <th className="text-left py-1 font-medium">Detail</th>
              </tr></thead>
              <tbody>
                {(r.timeline || []).map((e, i) => (
                  <tr key={i} className={`border-t border-zinc-100 ${e.bot ? "text-zinc-400" : "text-zinc-700"}`}>
                    <td className="py-1 capitalize">
                      {e.type.replace("email.", "")}
                      {e.inferred && <span className="ml-1 text-[9px] text-sky-600 border border-sky-200 bg-sky-50 rounded px-1">inferred</span>}
                      {e.bot && <span className="ml-1 text-[9px] text-amber-600 border border-amber-200 bg-amber-50 rounded px-1">scanner{e.bot_reason ? `: ${REASON_LABELS[e.bot_reason] || e.bot_reason}` : ""}</span>}
                    </td>
                    <td className="py-1 text-zinc-500">{fmt(e.created_at)}</td>
                    <td className="py-1 truncate max-w-[380px]">{e.url ? e.url.replace(/^https?:\/\//, "") : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
      {profileOpen && <ContactQuickView contactId={r.contact_id} onClose={() => setProfileOpen(false)} />}
    </>
  );
}

const SummaryRow = ({ label, value, ok, onEdit }) => (
  <div className="flex items-center gap-3 py-2">
    {ok ? <CheckCircle size={16} weight="fill" className="text-emerald-600 shrink-0"/> : <Circle size={16} className="text-rose-400 shrink-0"/>}
    <span className="text-xs text-zinc-500 w-24 shrink-0">{label}</span>
    <span className={`text-sm truncate flex-1 ${ok ? "text-zinc-900" : "text-rose-600"}`}>{value}</span>
    {onEdit && <button onClick={onEdit} className="text-xs text-zinc-500 hover:text-zinc-950 underline shrink-0">Edit</button>}
  </div>
);

const Metric = ({ label, value, sub }) => (
  <div className="rounded-lg border border-zinc-200 bg-white p-4">
    <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">{label}</div>
    <div className="mt-2 font-heading text-2xl font-medium text-zinc-950">{value}</div>
    {sub && <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>}
  </div>
);

const HEALTH_COLOR = {
  good: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", dot: "bg-emerald-500" },
  warn: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", dot: "bg-amber-500" },
  bad: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", dot: "bg-rose-500" },
};
const HEALTH_LABEL = { good: "Healthy", warn: "Watch", bad: "Critical" };

const CampaignHealthMetric = ({ label, data, suffix }) => {
  const c = HEALTH_COLOR[data.grade] || HEALTH_COLOR.good;
  return (
    <div className={`rounded-md border ${c.border} ${c.bg} p-3`}>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-600">{label}</div>
      <div className="flex items-baseline gap-1.5 mt-1.5">
        <span className={`font-heading text-2xl font-medium ${c.text}`}>{data.rate}%</span>
        <span className="text-[10px] text-zinc-500">good ≤ {data.threshold_good}% · watch ≤ {data.threshold_warn}%</span>
      </div>
      <div className="flex items-center gap-1.5 mt-1.5">
        <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
        <span className={`text-[11px] font-medium ${c.text}`}>{HEALTH_LABEL[data.grade]}</span>
        <span className="text-[11px] text-zinc-500 ml-auto">{suffix}</span>
      </div>
    </div>
  );
};
